/**
 * VehicleFinancingPlans — TEST-STAGE financing/installment plans on
 * eligible vehicle listings.
 *
 * SCOPE (deliberately minimal):
 *  - Fetches /api/platform/public/vehicles/{id}/financing-plans
 *  - Renders NOTHING if the backend says the listing is ineligible
 *    (individual sellers, unverified businesses, non-dealer categories).
 *  - Renders NOTHING if no plans are configured.
 *  - Displays each plan's configured details (provider, months,
 *    installment, down payment, duration, total, terms) — CARTAL does
 *    NOT invent any of these numbers; they come from the plan's `spec`.
 *  - A tiny in-place calculator recomputes values from the vehicle
 *    price + the plan's configured percentages, so the user sees the
 *    exact numbers for this listing.
 *  - "Select this plan" opens a modal that submits a finance_inquiry
 *    request via the EXISTING /platform/comms/requests endpoint,
 *    tagging the payload with the selected plan + vehicle.
 *
 * Reuses:
 *  - platform_products (kind = finance_product | bnpl_plan | leasing_product)
 *  - /platform/comms/requests (existing request flow)
 *  - toast / apiClient / auth / i18n helpers
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, HandCoins, X, Send, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { apiClient, formatSAR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const KIND_ICON = {
  finance_product: HandCoins,
  bnpl_plan:       CreditCard,
  leasing_product: HandCoins,
};

const KIND_LABEL = {
  finance_product: { ar: "تمويل",    en: "Financing" },
  bnpl_plan:       { ar: "تقسيط",    en: "BNPL" },
  leasing_product: { ar: "تأجير تمويلي", en: "Leasing" },
};

const PROVIDER_LABEL = {
  tabby:  { ar: "Tabby (تجريبي)",   en: "Tabby (test-stage)" },
  tamara: { ar: "Tamara (تجريبي)",  en: "Tamara (test-stage)" },
};

function computePlan(price, spec) {
  const p = Number(price) || 0;
  const months = Number(spec?.months) || 0;
  const downPct = Number(spec?.down_payment_pct) || 0;
  const aprPct = Number(spec?.apr_pct) || 0;
  const feeSar = Number(spec?.fee_sar) || 0;
  const down = Math.round((downPct / 100) * p);
  const financed = Math.max(0, p - down);
  // Simple flat/annual approximation. CARTAL does NOT invent rates —
  // apr_pct comes from the plan spec configured by the provider.
  const totalInterest = months > 0 ? Math.round(financed * (aprPct / 100) * (months / 12)) : 0;
  const totalPayable = financed + totalInterest + feeSar;
  const installment = months > 0 ? Math.round(totalPayable / months) : totalPayable;
  return { down, financed, totalInterest, totalPayable, installment, months };
}

export default function VehicleFinancingPlans({ vehicleId }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await apiClient.get(`/platform/public/vehicles/${vehicleId}/financing-plans`);
        if (alive) setData(d);
      } catch {
        if (alive) setData({ eligible: false, items: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [vehicleId]);

  if (loading) return null;
  if (!data?.eligible) return null;
  if (!data.items?.length) return null;

  const price = data.vehicle?.price_sar || 0;

  return (
    <div className="mt-8 rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="vehicle-financing-plans">
      <div className="flex items-center gap-2 mb-1">
        <HandCoins className="w-4 h-4 text-[#8a6f2e]" />
        <div className="overline overline-gold">{lang === "ar" ? "خطط التمويل والتقسيط" : "Financing & Installment plans"}</div>
      </div>
      <h3 className="font-display text-2xl mb-1">
        {lang === "ar" ? "مقدَّمة من " : "Provided by "}
        <span className="text-[#8a6f2e]">
          {lang === "ar" ? data.business?.name_ar : (data.business?.name_en || data.business?.name_ar)}
        </span>
      </h3>
      <div className="text-[12px] text-[#8a8a8a] mb-4 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        {lang === "ar"
          ? "الأرقام أدناه تجريبية للاختبار وتأتي من خطط الممول المعتمد. لا تعكس اتفاقية رسمية مع Tabby أو Tamara."
          : "Test-stage figures — provider-configured. Not an official CARTAL partnership with Tabby or Tamara."}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {data.items.map((p) => {
          const Icon = KIND_ICON[p.kind] || CreditCard;
          const calc = computePlan(price, p.spec);
          const provider = p.spec?.provider;
          const isTest = p.spec?.is_test === true;
          return (
            <div
              key={p.id}
              className="rounded-xl border p-4"
              style={{ borderColor: "#c9a55c33", background: "#fdf9ef" }}
              data-testid={`plan-${p.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center border border-[#c9a55c]/40 shrink-0">
                  <Icon className="w-4 h-4 text-[#8a6f2e]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-bold text-[#0a0a0a]" data-testid={`plan-${p.id}-name`}>
                    {lang === "ar" ? p.name_ar : (p.name_en || p.name_ar)}
                  </div>
                  <div className="text-[11px] text-[#8a8a8a] mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span>{lang === "ar" ? KIND_LABEL[p.kind]?.ar : KIND_LABEL[p.kind]?.en}</span>
                    {provider && PROVIDER_LABEL[provider] && (
                      <>
                        <span>·</span>
                        <span className="font-semibold text-[#8a6f2e]" data-testid={`plan-${p.id}-provider`}>
                          {lang === "ar" ? PROVIDER_LABEL[provider].ar : PROVIDER_LABEL[provider].en}
                        </span>
                      </>
                    )}
                    {isTest && (
                      <>
                        <span>·</span>
                        <span className="px-1.5 py-0.5 rounded-full font-bold text-[9px] uppercase tracking-widest"
                              style={{ background: "#fef3c7", color: "#8a6f2e" }}
                              data-testid={`plan-${p.id}-test-badge`}>
                          {lang === "ar" ? "خطة تجريبية" : "TEST-STAGE"}
                        </span>
                      </>
                    )}
                  </div>

                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px]">
                    {calc.months > 0 && (
                      <>
                        <dt className="text-[#8a8a8a]">{lang === "ar" ? "المدة" : "Duration"}</dt>
                        <dd className="text-[#0a0a0a] font-semibold text-end" data-testid={`plan-${p.id}-months`}>
                          {calc.months} {lang === "ar" ? "شهر" : "months"}
                        </dd>
                      </>
                    )}
                    {calc.down > 0 && (
                      <>
                        <dt className="text-[#8a8a8a]">{lang === "ar" ? "الدفعة الأولى" : "Down payment"}</dt>
                        <dd className="text-[#0a0a0a] font-semibold text-end" data-testid={`plan-${p.id}-down`}>
                          {formatSAR(calc.down)} SAR
                        </dd>
                      </>
                    )}
                    <dt className="text-[#8a8a8a]">{lang === "ar" ? "القسط الشهري" : "Monthly installment"}</dt>
                    <dd className="text-[#8a6f2e] font-bold text-end" data-testid={`plan-${p.id}-installment`}>
                      {formatSAR(calc.installment)} SAR
                    </dd>
                    <dt className="text-[#8a8a8a]">{lang === "ar" ? "الإجمالي" : "Total payable"}</dt>
                    <dd className="text-[#0a0a0a] font-semibold text-end" data-testid={`plan-${p.id}-total`}>
                      {formatSAR(calc.totalPayable)} SAR
                    </dd>
                  </dl>

                  {p.spec?.terms_ar && (
                    <div className="mt-3 text-[11px] text-[#525252] leading-relaxed">
                      {lang === "ar" ? p.spec.terms_ar : (p.spec.terms_en || p.spec.terms_ar)}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setSelected({ plan: p, calc })}
                    className="mt-4 px-4 py-2 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
                    style={{ background: "#0a0a0a", color: "#e8c873" }}
                    data-testid={`plan-${p.id}-select`}
                  >
                    <Send className="w-3.5 h-3.5" />
                    {lang === "ar" ? "اختر هذه الخطة" : "Select this plan"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <SelectPlanModal
          onClose={() => setSelected(null)}
          plan={selected.plan}
          calc={selected.calc}
          business={data.business}
          vehicle={data.vehicle}
          user={user}
          lang={lang}
        />
      )}
    </div>
  );
}

function SelectPlanModal({ plan, calc, business, vehicle, user, lang, onClose }) {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");

  const submit = async () => {
    if (!user) return;
    if (!phone.trim()) {
      toast.error(lang === "ar" ? "أدخل رقم جوالك" : "Enter your phone");
      return;
    }
    setBusy(true);
    try {
      const providerLabel = plan.spec?.provider
        ? (PROVIDER_LABEL[plan.spec.provider]?.ar || plan.spec.provider)
        : (lang === "ar" ? "الممول المعتمد" : "provider");
      const planName = plan.name_ar || plan.name_en;
      const body =
        `طلب تمويل للمركبة #${vehicle.id}\n` +
        `الخطة المختارة: ${planName} (${providerLabel})\n` +
        `المدة: ${calc.months} شهر · الدفعة الأولى: ${calc.down} SAR · القسط الشهري: ${calc.installment} SAR\n` +
        (note ? `\nملاحظة العميل: ${note}` : "");
      await apiClient.post("/platform/comms/requests", {
        kind: "finance_inquiry",
        business_id: business.id,
        vehicle_id: vehicle.id,
        subject: `${planName} — ${lang === "ar" ? "مركبة" : "vehicle"} #${vehicle.id}`,
        body,
        customer_phone: phone,
        payload: {
          test_stage: !!plan.spec?.is_test,
          provider: plan.spec?.provider || null,
          plan_id: plan.id,
          plan_kind: plan.kind,
          months: calc.months,
          down_payment_sar: calc.down,
          installment_sar: calc.installment,
          total_sar: calc.totalPayable,
          vehicle_id: vehicle.id,
          vehicle_price_sar: vehicle.price_sar,
        },
      });
      setOk(true);
      toast.success(lang === "ar" ? "تم إرسال طلبك للممول" : "Request sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "ar" ? "تعذّر الإرسال" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="plan-select-modal">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg">
            {lang === "ar" ? "إرسال طلب التمويل" : "Send financing request"}
          </h4>
          <button onClick={onClose} className="p-1"><X className="w-4 h-4" /></button>
        </div>
        {ok ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
            <div className="text-[14px] text-[#0a0a0a] font-bold">
              {lang === "ar" ? "تم إرسال طلبك بنجاح" : "Your request was sent"}
            </div>
            <div className="text-[12px] text-[#8a8a8a] mt-1">
              {lang === "ar" ? "يمكنك متابعة الحالة من صفحة طلباتي." : "Track status from My Requests."}
            </div>
            <Link to="/orders" className="btn-primary mt-4 inline-flex" onClick={onClose}>
              {lang === "ar" ? "الذهاب إلى طلباتي" : "Go to My Requests"}
            </Link>
          </div>
        ) : !user ? (
          <div className="text-center py-4">
            <div className="text-[13px] text-[#525252]">
              {lang === "ar" ? "يجب تسجيل الدخول لإرسال طلب التمويل." : "Please log in to send a financing request."}
            </div>
            <Link to="/login" className="btn-primary mt-3 inline-flex">
              {lang === "ar" ? "تسجيل الدخول" : "Log in"}
            </Link>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-[#fdf9ef] p-3 text-[12px] text-[#525252] mb-3 leading-relaxed">
              <div><span className="font-bold text-[#0a0a0a]">{plan.name_ar || plan.name_en}</span></div>
              <div>{lang === "ar" ? "المدة" : "Duration"}: {calc.months} {lang === "ar" ? "شهر" : "months"}</div>
              <div>{lang === "ar" ? "الدفعة الأولى" : "Down"}: {formatSAR(calc.down)} SAR · {lang === "ar" ? "القسط" : "Installment"}: {formatSAR(calc.installment)} SAR</div>
              <div>{lang === "ar" ? "الإجمالي" : "Total"}: {formatSAR(calc.totalPayable)} SAR</div>
            </div>
            <label className="block text-[12px] text-[#8a8a8a] mb-1">{lang === "ar" ? "رقم الجوال *" : "Phone *"}</label>
            <input
              className="input-dark w-full mb-3"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05xxxxxxxx"
              data-testid="plan-modal-phone"
            />
            <label className="block text-[12px] text-[#8a8a8a] mb-1">{lang === "ar" ? "ملاحظة (اختياري)" : "Note (optional)"}</label>
            <textarea
              className="input-dark w-full min-h-[70px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="plan-modal-note"
            />
            <div className="flex gap-2 justify-end mt-3">
              <button onClick={onClose} className="btn-ghost">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button onClick={submit} disabled={busy} className="btn-primary" data-testid="plan-modal-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {lang === "ar" ? "إرسال الطلب" : "Send request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
